
import { supabase } from '@/lib/supabase/client';
import { ProductData } from '../types/productTypes';

// Function to fetch videos from Supabase
export const fetchVideosFromSupabase = async (profileId: string): Promise<ProductData[]> => {
  try {
    const { data: videosData, error: videosError } = await supabase
      .from('videos')
      .select('*, products(id, name)')
      .eq('profile_id', profileId)
      .order('published_at', { ascending: false });
      
    if (videosError) {
      console.error('Error fetching videos from Supabase:', videosError);
      return [];
    }
    
    console.log('Videos fetched from Supabase:', videosData);
    
    // Map the Supabase videos data to our ProductData format
    return videosData.map(video => ({
      id: video.id,
      title: video.title || video.video_title || 'Vidéo YouTube',
      image: video.thumbnail_url || `https://img.youtube.com/vi/${video.youtube_id || video.video_id || 'dQw4w9WgXcQ'}/maxresdefault.jpg`,
      videoId: video.youtube_id || video.video_id || 'dQw4w9WgXcQ',
      publishedAt: video.published_at || new Date().toISOString(),
      productType: 'Vidéo',
      price: 0,
      currency: 'EUR',
      profileId: video.profile_id || profileId,
      attachments: 2,
      views: video.view_count || 0,
      likes: video.like_count || 0,
      productId: video.product_id || null,
      // Views and likes for compatibility
      viewCount: video.view_count || 0,
      likeCount: video.like_count || 0,
      // For compatibility with linkedProduct
      linkedProduct: video.products ? {
        id: video.products.id,
        name: video.products.name,
        title: video.products.name,
        image: video.products.media_urls?.[0] || ''
      } : undefined,
      // Name for compatibility with Product interface
      name: video.title || video.video_title || 'Vidéo YouTube',
      // Tags and type for VideoProductCard
      type: 'video' as const,
      tags: []
    }));
  } catch (error) {
    console.error('Error fetching videos:', error);
    return [];
  }
};

// Function to fetch a video by ID from Supabase
export const fetchVideoById = async (videoId: string): Promise<ProductData | null> => {
  try {
    const { data: video, error } = await supabase
      .from('videos')
      .select('*, products(id, name, media_urls)')
      .eq('id', videoId)
      .single();
      
    if (error) {
      console.error('Error fetching video by ID:', error);
      return null;
    }
    
    return {
      id: video.id,
      title: video.title || video.video_title || 'Vidéo YouTube',
      name: video.title || video.video_title || 'Vidéo YouTube',
      image: video.thumbnail_url || `https://img.youtube.com/vi/${video.youtube_id || video.video_id || 'dQw4w9WgXcQ'}/maxresdefault.jpg`,
      videoId: video.youtube_id || video.video_id || 'dQw4w9WgXcQ',
      publishedAt: video.published_at || new Date().toISOString(),
      productType: 'Vidéo',
      price: 0,
      currency: 'EUR',
      profileId: video.profile_id || '',
      attachments: 2,
      views: video.view_count || 0,
      likes: video.like_count || 0,
      viewCount: video.view_count || 0,
      likeCount: video.like_count || 0,
      productId: video.product_id || null,
      type: 'video' as const,
      tags: [],
      linkedProduct: video.products ? {
        id: video.products.id,
        name: video.products.name,
        title: video.products.name,
        image: video.products.media_urls?.[0] || ''
      } : undefined
    };
  } catch (error) {
    console.error(`Error fetching video with id ${videoId}:`, error);
    return null;
  }
};

// Function to link a video to a product
export const linkVideoToProduct = async (videoId: string, productId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('videos')
      .update({ product_id: productId })
      .eq('id', videoId);
    
    if (error) {
      console.error('Error linking video to product:', error);
      return false;
    }
    
    console.log(`Successfully linked video ${videoId} to product ${productId}`);
    return true;
  } catch (error) {
    console.error('Failed to link video to product:', error);
    return false;
  }
};
